import { Fragment, jsx, jsxs } from "react/jsx-runtime";
import { useContext, useEffect, useMemo, useState } from "react";
import { Absolute, Box, Button, Flex, FormField, Label, Relative, Spinner, Text, Tooltip } from "pcln-design-system";
import { ErrorToolTip, FormInput, SelectInput, SuccessIcon, WarningIcon } from "./OtpForm.styles.js";
import { uniqCountryCodes } from "./helpers/buildCountryDropdown.js";
import otpService from "../../utils/otpService.js";
import { useOtpContext } from "../../context/OtpContext.js";
import { ChatbotDataContext } from "../../context/ChatbotDataContext.js";
import { otpDeliveryMechanisms } from "../../constants/index.js";
const OtpForm = ()=>{
    const errorMessage = 'I’m having some trouble accessing your trip information. ';
    const [phoneColor, setPhoneColor] = useState();
    const [emailColor, setEmailColor] = useState();
    const [phoneNumber, setPhoneNumber] = useState();
    const [email, setEmail] = useState();
    const [countryCode, setCountryCode] = useState('1');
    const [otpFormSubmissionInProgress, setOtpFormSubmissionInProgress] = useState(false);
    const [otpDeliveryMechanism, setOtpDeliveryMechanism] = useState();
    const [showEmailInput, setShowEmailInput] = useState(false);
    const [showSmsInput, setShowSmsInput] = useState(true);
    const { otpDeliveryType, otpDeliveryMethodUsed, setOtpRequestErrorMsg, setVerificationApiFunction, setShowOtpVerificationForm, setOtpFormData, setOtpDeliveryMethodUsed } = useOtpContext();
    const { setAccountLessTripsAccessPayload, submitChatMessagesPayload, requestFrom } = useContext(ChatbotDataContext);
    const { cguid, resolvedClientIP } = submitChatMessagesPayload || {};
    useEffect(()=>{
        if (otpDeliveryType === otpDeliveryMechanisms.EMAIL_ONLY_OTP) {
            setShowEmailInput(true);
            setShowSmsInput(false);
            setOtpDeliveryMechanism(otpDeliveryMechanisms.EMAIL_ONLY_OTP);
        } else if (otpDeliveryType === otpDeliveryMechanisms.SMS) if ('1' === countryCode) {
            setShowEmailInput(false);
            setShowSmsInput(true);
            setOtpDeliveryMechanism(otpDeliveryMechanisms.SMS_PHONE);
        } else {
            setShowEmailInput(true);
            setShowSmsInput(true);
            setOtpDeliveryMechanism(otpDeliveryMechanisms.SMS);
        }
    }, [
        otpDeliveryType,
        countryCode
    ]);
    const isPhoneValidated = useMemo(()=>{
        if (!phoneNumber) return false;
        const isValidFormat = /^[0-9]+$/.test(phoneNumber) || /^\d{3}-\d{3}-\d{4}$/.test(phoneNumber);
        if (!isValidFormat) return false;
        const digits = phoneNumber.replace(/-/g, '');
        if ('1' === countryCode) return 10 === digits.length;
        return digits.length >= 5;
    }, [
        phoneNumber,
        countryCode
    ]);
    const isEmailValidated = useMemo(()=>/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email), [
        email
    ]);
    const disableButton = useMemo(()=>{
        if (otpDeliveryType === otpDeliveryMechanisms.SMS && '1' === countryCode) return !isPhoneValidated;
        if (otpDeliveryType === otpDeliveryMechanisms.EMAIL || otpDeliveryType === otpDeliveryMechanisms.SMS) return !isPhoneValidated || !isEmailValidated;
        if (otpDeliveryType === otpDeliveryMechanisms.EMAIL_ONLY_OTP) return !isEmailValidated;
    }, [
        isEmailValidated,
        isPhoneValidated,
        countryCode,
        otpDeliveryType
    ]);
    function checkPhoneValidation() {
        if (phoneNumber) setPhoneColor(isPhoneValidated ? 'success' : 'error');
    }
    function checkEmailValidation() {
        if (email) setEmailColor(isEmailValidated ? 'success' : 'error');
    }
    function handlePhoneChange(e) {
        const value = e.currentTarget.value;
        if (/^\d*$/.test(value)) setPhoneNumber(value);
    }
    function handleEmailChange(e) {
        const value = e.currentTarget.value;
        setEmail(value);
    }
    async function submitFormHandler(e) {
        e.preventDefault();
        setOtpRequestErrorMsg('');
        setOtpFormData({
            ...phoneNumber && {
                phoneNumber: Number(phoneNumber)
            },
            ...countryCode && {
                countryCode: countryCode
            },
            ...email && {
                email: email
            }
        });
        setAccountLessTripsAccessPayload(otpDeliveryMechanism === otpDeliveryMechanisms.EMAIL_ONLY_OTP ? {
            emailAddress: email,
            scope: 'TRIPS'
        } : otpDeliveryMechanism === otpDeliveryMechanisms.SMS_PHONE ? {
            phoneNumber: {
                number: phoneNumber,
                countryCode
            },
            scope: 'TRIPS'
        } : {
            emailAddress: email,
            phoneNumber: {
                number: phoneNumber,
                countryCode
            },
            scope: 'TRIPS'
        });
        const otpDeliveryMethodUsed = otpDeliveryMechanism === otpDeliveryMechanisms.SMS_PHONE || otpDeliveryMechanism === otpDeliveryMechanisms.SMS ? otpDeliveryMechanisms.SMS : otpDeliveryMechanisms.EMAIL;
        setOtpDeliveryMethodUsed(otpDeliveryMethodUsed);
        setOtpFormSubmissionInProgress(true);
        setOtpRequestErrorMsg('');
        try {
            const otpServiceInstance = otpService({
                deliveryType: otpDeliveryMechanism
            });
            if (otpServiceInstance.verify) setVerificationApiFunction(()=>otpServiceInstance.verify);
            const otpChallenge = await otpServiceInstance.createChallenge({
                phoneNumber,
                email,
                countryCode,
                scope: 'trips',
                requestHeaders: {
                    'x-pcln-client-ip': resolvedClientIP,
                    cguid
                },
                requestFrom
            });
            if (otpChallenge) setShowOtpVerificationForm(true);
            else setOtpRequestErrorMsg(errorMessage);
        } catch (_e) {
            setOtpRequestErrorMsg(errorMessage);
        } finally{
            setOtpFormSubmissionInProgress(false);
        }
    }
    return /*#__PURE__*/ jsx(Fragment, {
        children: /*#__PURE__*/ jsx(Box, {
            maxWidth: "390px",
            children: otpFormSubmissionInProgress ? /*#__PURE__*/ jsx(Spinner, {}) : /*#__PURE__*/ jsxs(Box, {
                children: [
                    /*#__PURE__*/ jsxs(Flex, {
                        flexDirection: 'column',
                        mb: 2,
                        children: [
                            showSmsInput && /*#__PURE__*/ jsxs(Flex, {
                                alignItems: "center",
                                children: [
                                    /*#__PURE__*/ jsx(Box, {
                                        width: '30%',
                                        mr: 2,
                                        children: /*#__PURE__*/ jsxs(Relative, {
                                            children: [
                                                /*#__PURE__*/ jsxs(FormField, {
                                                    children: [
                                                        /*#__PURE__*/ jsx(Label, {
                                                            htmlFor: "countryCodes",
                                                            children: 'Country Code \u207A'
                                                        }),
                                                        /*#__PURE__*/ jsx(SelectInput, {
                                                            defaultValue: "1",
                                                            id: "countryCodes",
                                                            name: "countryCodes",
                                                            value: countryCode,
                                                            onChange: function(e) {
                                                                setCountryCode(e.target.value);
                                                            },
                                                            children: uniqCountryCodes.map((country)=>/*#__PURE__*/ jsx("option", {
                                                                    value: country.phoneCode,
                                                                    children: country.label
                                                                }, country.label))
                                                        }),
                                                        /*#__PURE__*/ jsx(SuccessIcon, {
                                                            color: "success"
                                                        })
                                                    ]
                                                }),
                                                /*#__PURE__*/ jsx(Absolute, {
                                                    bg: "background.lightest",
                                                    top: 17,
                                                    left: 14,
                                                    width: "75px",
                                                    style: {
                                                        pointerEvents: 'none'
                                                    },
                                                    children: /*#__PURE__*/ jsx(Box, {
                                                        children: /*#__PURE__*/ jsxs(Text, {
                                                            fontSize: 1,
                                                            children: [
                                                                "+",
                                                                countryCode
                                                            ]
                                                        })
                                                    })
                                                })
                                            ]
                                        })
                                    }),
                                    /*#__PURE__*/ jsxs(Box, {
                                        width: '70%',
                                        children: [
                                            /*#__PURE__*/ jsxs(FormField, {
                                                children: [
                                                    /*#__PURE__*/ jsx(Label, {
                                                        htmlFor: "phone",
                                                        children: 'Phone Number \u207A'
                                                    }),
                                                    /*#__PURE__*/ jsx(FormInput, {
                                                        id: "phone",
                                                        name: "phone",
                                                        type: "text",
                                                        inputMode: "numeric",
                                                        pattern: "[0-9]*",
                                                        placeholder: 'Phone number \u207A',
                                                        onChange: handlePhoneChange,
                                                        onFocus: function() {
                                                            setPhoneColor(void 0);
                                                        },
                                                        onBlur: checkPhoneValidation,
                                                        color: phoneColor,
                                                        value: phoneNumber,
                                                        "aria-describedby": "phone-error"
                                                    }),
                                                    'error' === phoneColor ? /*#__PURE__*/ jsx(WarningIcon, {
                                                        color: "error"
                                                    }) : null,
                                                    'success' === phoneColor ? /*#__PURE__*/ jsx(SuccessIcon, {
                                                        color: "success"
                                                    }) : null
                                                ]
                                            }),
                                            'error' === phoneColor ? /*#__PURE__*/ jsx(ErrorToolTip, {
                                                id: "phone-error",
                                                right: true,
                                                color: "error",
                                                children: "Phone number is invalid"
                                            }) : null
                                        ]
                                    })
                                ]
                            }),
                            showEmailInput && /*#__PURE__*/ jsxs(Box, {
                                mt: showSmsInput ? 2 : 0,
                                children: [
                                    /*#__PURE__*/ jsxs(FormField, {
                                        children: [
                                            /*#__PURE__*/ jsx(Label, {
                                                htmlFor: "email",
                                                children: 'Email Address \u207A'
                                            }),
                                            /*#__PURE__*/ jsx(FormInput, {
                                                id: "email",
                                                name: "email",
                                                placeholder: 'Email address \u207A',
                                                onChange: handleEmailChange,
                                                onFocus: function() {
                                                    setEmailColor(void 0);
                                                },
                                                onBlur: checkEmailValidation,
                                                color: emailColor,
                                                value: email
                                            }),
                                            'error' === emailColor ? /*#__PURE__*/ jsx(WarningIcon, {
                                                color: "error"
                                            }) : null,
                                            'success' === emailColor ? /*#__PURE__*/ jsx(SuccessIcon, {
                                                color: "success"
                                            }) : null
                                        ]
                                    }),
                                    'error' === emailColor ? /*#__PURE__*/ jsx(Tooltip, {
                                        id: "email-error",
                                        right: true,
                                        color: "error",
                                        children: "Email is invalid"
                                    }) : null
                                ]
                            })
                        ]
                    }),
                    /*#__PURE__*/ jsx(Box, {
                        mt: 1,
                        children: /*#__PURE__*/ jsx(Button, {
                            disabled: disableButton,
                            onClick: submitFormHandler,
                            children: "Continue"
                        })
                    })
                ]
            })
        })
    });
};
const OtpForm_OtpForm = OtpForm;
export { OtpForm_OtpForm as default };
